<script setup lang="ts">
  // The account menu: a drawer from the right, opened from the avatar in the header (see app-header.vue).
  // Everything that is about the user and not about the event: settings, help, legal, the colour
  // theme and sign out. Notifications are the bell in the header, not a row here. The event sections are the hamburger drawer, main-menu.vue.
  import {VueFinalModal} from 'vue-final-modal';

  const route = useRoute();
  const config = useRuntimeConfig();
  const {fixedWidthImageParams} = useImageCdn();
  const {data} = useAuth();
  const {accountOpen: open, available} = useMainMenu();
  const {signOutUser} = useSignOutUser();

  const user = computed(() => data.value?.data ?? null);
  const routeName = computed(() => <string>route.name);

  // Theme switcher API provided by plugins/theme.global.ts
  interface ThemeApi {
    set: (option: 'light' | 'dark' | 'system') => void;
    currentOption: Ref<'light' | 'dark' | 'system'>;
  }
  const themeApi = useNuxtApp().$theme as ThemeApi | undefined;
  const themeOption = computed(() => themeApi?.currentOption?.value ?? 'system');
  const THEMES: Array<{value: 'light' | 'system' | 'dark', label: string}> = [
    {value: 'light', label: 'Light'},
    {value: 'system', label: 'System'},
    {value: 'dark', label: 'Dark'},
  ];

  const helpUrl = 'https://www.wearedevelopers.com/about/contact-us';
  const platformUrl = computed(() => config.public.platformUrl);

  // Close when navigating, whichever way the navigation started.
  watch(() => route.fullPath, () => { open.value = false; });
  // Never stay open once the menu is gone (logout).
  watch(available, (isAvailable) => { if(!isAvailable) open.value = false; });

  const close = () => { open.value = false; };
  const onSignOut = () => signOutUser(close);
</script>

<template>
  <VueFinalModal v-if="available && user"
                 v-model="open"
                 teleport-to="body"
                 display-directive="if"
                 overlay-transition="vfm-fade"
                 content-transition="wad4-drawer-slide-right"
                 content-class="wad4-account-menu-vfm"
                 :click-to-close="true"
                 :esc-to-close="true"
                 :lock-scroll="true">
    <nav class="wad4-account-menu" aria-label="Account menu">
      <header class="wad4-account-menu__head">
        <img class="wad4-account-menu__avatar" v-bind="fixedWidthImageParams(user.image_url, 44, 1)" :alt="user.full_name">
        <span class="wad4-account-menu__name">{{ user.full_name }}</span>
        <button type="button" class="wad4-account-menu__close" aria-label="Close menu" @click="close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </header>

      <div class="wad4-account-menu__body">
        <ul class="wad4-account-menu__list">
          <li>
            <nuxt-link :to="{name: 'index'}" class="wad4-account-menu__link" :class="{'wad4-account-menu__link--active': routeName === 'index'}" @click="close">{{ SWITCH_EVENT_LABEL }}</nuxt-link>
          </li>
          <li>
            <nuxt-link :to="{name: 'settings'}" class="wad4-account-menu__link" :class="{'wad4-account-menu__link--active': routeName === 'settings'}" @click="close">Settings</nuxt-link>
          </li>
          <li><a href="javascript:Truendo.openCookieSettings()" class="wad4-account-menu__link">Cookies</a></li>
          <li>
            <nuxt-link :to="{name: 'legal'}" class="wad4-account-menu__link" :class="{'wad4-account-menu__link--active': routeName === 'legal'}" @click="close">Legal information</nuxt-link>
          </li>
          <li><a :href="helpUrl" target="_blank" rel="noopener" class="wad4-account-menu__link">Help<common-external-link-icon/></a></li>
          <li><a :href="platformUrl" target="_blank" rel="noopener" class="wad4-account-menu__link">Go to WeAreDevelopers.com<common-external-link-icon/></a></li>
        </ul>

        <div class="wad4-account-menu__foot">
          <div class="wad4-account-menu__theme" role="group" aria-label="Color theme">
            <button v-for="theme in THEMES" :key="theme.value" type="button" class="wad4-account-menu__theme-option"
                    :class="{'wad4-account-menu__theme-option--active': themeOption === theme.value}"
                    :aria-pressed="themeOption === theme.value"
                    @click="themeApi?.set(theme.value)">
              {{ theme.label }}
            </button>
          </div>

          <button type="button" class="wad4-account-menu__signout" @click="onSignOut">Sign out</button>
        </div>
      </div>
    </nav>
  </VueFinalModal>
</template>

<style lang="scss">
// VFM content element: full-screen flex container that right-anchors the drawer, so the slide
// transform has room to move it. It must not swallow taps on the dimmed area beside the drawer,
// those fall through to the vfm root so click-to-close fires.
.wad4-account-menu-vfm {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
}

.wad4-account-menu {
  display: flex;
  flex-direction: column;
  width: min(86vw, 340px);
  height: 100%;
  pointer-events: auto;
  background: $wad4-c-background-bg-primary;
  color: $wad4-c-text-text-primary;
  border-left: 1px solid $wad4-c-border-border-subtle;
  // Below the iOS notch and above the home indicator in standalone PWA mode.
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);

  &__head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: $wad4-spacing-lg;
    padding: $wad4-spacing-lg $wad4-container-padding;
    border-bottom: 1px solid $wad4-c-border-border-subtle;
  }

  &__avatar {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: $wad4-border-radius-full;
    object-fit: cover;
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include text-size-mixin('lg');
    font-weight: $wad4-font-weight-semi-bold;
  }

  &__close {
    flex-shrink: 0;
    display: inline-flex;
    // 10px + 24px icon + 10px = a 44px tap target.
    padding: 10px;
    border: none;
    border-radius: $wad4-border-radius-md;
    background: none;
    color: $wad4-c-foreground-fg-primary;
    cursor: pointer;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }
  }

  // The head stays put; only the body scrolls, so the name and close button never leave.
  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    padding-bottom: $wad4-spacing-xl;
  }

  &__list {
    margin: 0;
    padding: $wad4-spacing-md 0;
    list-style: none;
  }

  &__link {
    display: flex;
    align-items: center;
    padding: $wad4-spacing-lg $wad4-container-padding;
    color: $wad4-c-text-text-primary;
    @include text-size-mixin('lg');
    text-decoration: none;
    cursor: pointer;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }

    &--active {
      color: $wad4-c-text-text-brand-tertiery;
      font-weight: $wad4-font-weight-semi-bold;
    }
  }

  // Theme and sign out sit at the bottom when the list above is short.
  &__foot {
    margin-top: auto;
    padding-top: $wad4-spacing-lg;
    border-top: 1px solid $wad4-c-border-border-subtle;
  }

  &__theme {
    display: flex;
    gap: $wad4-spacing-xs;
    margin: 0 $wad4-container-padding;
    padding: $wad4-spacing-xs;
    border-radius: $wad4-border-radius-md;
    background: $wad4-c-background-bg-secondary;
  }

  &__theme-option {
    flex: 1;
    padding: $wad4-spacing-sm $wad4-spacing-md;
    border: 1px solid transparent;
    border-radius: $wad4-spacing-sm;
    background: transparent;
    color: $wad4-c-text-text-secondary;
    @include text-size-mixin('sm');
    font-weight: $wad4-font-weight-regular;
    cursor: pointer;

    &--active {
      background: $wad4-c-background-bg-primary;
      border-color: $wad4-c-border-border-subtle;
      color: $wad4-c-text-text-primary;
      box-shadow: $wad4-box-shadow-xs;
    }
  }

  &__signout {
    display: block;
    width: calc(100% - 2 * #{$wad4-container-padding});
    margin: $wad4-spacing-lg $wad4-container-padding 0;
    padding: $wad4-spacing-md;
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-md;
    background: $wad4-c-background-bg-primary;
    color: $wad4-c-text-text-primary;
    @include text-size-mixin('md');
    font-weight: $wad4-font-weight-semi-bold;
    cursor: pointer;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }
  }
}

.wad4-drawer-slide-right-enter-active,
.wad4-drawer-slide-right-leave-active {
  transition: transform 0.25s ease;
}
.wad4-drawer-slide-right-enter-from,
.wad4-drawer-slide-right-leave-to {
  transform: translateX(100%);
}
</style>
